import { AUTOMATION_GRADES, AUTOMATION_GRADE_TEXT, PROPOSAL_WHO_TEXT, type AutomationGrade, type ClosingProposalDto, type ContractTwoOfferDto, type EngagementCloseDto, type PhaseElevenDto, type RangeDto, type TaskMapDto, type TaskProcessDto, type TaskSumDto, type TaskValueDto } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { ClosingFrame, Kpi, Rows, useClosingPhase, type ClosingPageProps } from './Fase10';
import { Progetto } from './Progetto';

/**
 * La fase 11 «Le proposte, i benefici e il percorso di realizzazione» (contratto n. 1 versione 2.2, art. 8, fase 11; ADR
 * 0179), identica alla pagina di prova docs/mockup/percorso-sinapsi/14-fase11: le proposte nel percorso, in ordine di
 * priorità, con il calcolo dello studio sotto ogni numero; le ore liberate per reparto e per ruolo; la mappa «Timonio
 * nell'azienda» con i compiti per ruolo e grado (art. 3.47); le domande a intervalli; il documento scaricabile (art. 4.2), «Confermo e chiudo l'incarico» (art. 12.2) e l'offerta del contratto
 * n. 2 con il link di prova dei ruoli (art. 4.4).
 */
export function Fase11Page(props: ClosingPageProps) {
  const p = useClosingPhase<PhaseElevenDto>(11);
  const [doc, setDoc] = useState(false);
  const [dettaglio, setDettaglio] = useState(false);
  return (
    <>
      <ClosingFrame
        p={p}
        props={props}
        eyebrow="Fase 11 · Proposte e percorso"
        working="Scrivo le proposte e calcolo i benefici"
        title={(d) => `Come chiudere ogni scarto, cosa ci guadagna ${d.azienda ?? 'l’azienda'}, in che ordine`}
        intro={() => 'Solo le ipotesi che pesano di più sui benefici, a intervalli. Le ipotesi che non ti chiedo restano stime del settore, scritte sotto ogni calcolo. La conferma di questa fase chiude l’incarico.'}
        footer={() => <Offer />}
        confirmLabel="Confermo e chiudo l’incarico"
        extra={
          <button type="button" className="sn-btn ghost sm" onClick={() => setDoc(true)}>
            Il progetto, da leggere e salvare in PDF
          </button>
        }
      >
        {(d) => (
          <>
            <ProposalsSummary d={d} />
            <section className="pan">
              <div className="hd">
                <h2 className="h2">Allegato · Il dettaglio delle proposte</h2>
                <button type="button" className="btn2" aria-expanded={dettaglio} onClick={() => setDettaglio((x) => !x)}>
                  {dettaglio ? 'Chiudi l’allegato' : 'Apri l’allegato'}
                </button>
              </div>
              <p className="note">Ogni proposta con il calcolo e le ipotesi, il percorso, le ore per reparto e per ruolo, la mappa «Timonio nell’azienda» per processo e per ruolo.</p>
            </section>
            {dettaglio && <Proposals d={d} kpi={false} />}
          </>
        )}
      </ClosingFrame>
      {doc && <Progetto onClose={() => setDoc(false)} />}
    </>
  );
}

const it = (n: number, d = 1) => n.toLocaleString('it-IT', { maximumFractionDigits: d });
const range = (r: RangeDto, d = 1) => (r.a == null ? `oltre ${it(r.da, d)}` : r.da === r.a ? it(r.da, d) : `${it(r.da, d)}-${it(r.a, d)}`);
const nn = (n: number) => String(n).padStart(2, '0');

/** Il corpo delle proposte; `tutte`: ogni proposta aperta, come nel documento scaricabile (Progetto.tsx). */
export function Proposals({ d, tutte = false, kpi = true }: { d: PhaseElevenDto; tutte?: boolean; kpi?: boolean }) {
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const shown = d.proposte.filter((p) => tutte || p.aperta);
  const others = tutte ? [] : d.proposte.filter((p) => !p.aperta);
  return (
    <>
      {kpi && <Totals d={d} />}

      <div className="hd">
        <h2 className="h2">Il percorso di realizzazione</h2>
      </div>
      <p className="sub">In ordine di priorità: prima gli obblighi scoperti, poi ciò che pesa di più in ore ed euro, poi ciò che riduce un rischio.</p>
      {shown.map((p) => (
        <Proposal key={p.n} p={p} />
      ))}

      {others.length > 0 && (
        <section className="pan">
          <div className="hd">
            <h2 className="h2">Le altre proposte</h2>
            <span className="lbl">CIASCUNA SI APRE COME QUELLE QUI SOPRA</span>
          </div>
          <div className="rows">
            <div className="row head" style={{ gridTemplateColumns: COLS }}>
              {['N.', 'SCARTO', 'PROPOSTA', 'CHI LA FA', 'PROGRAMMA', 'BENEFICIO'].map((h) => (
                <span key={h}>{h}</span>
              ))}
            </div>
            {others.map((p) => (
              <Fragment key={p.n}>
              <div className={`row click ${open[p.n] ? 'on' : ''}`} style={{ gridTemplateColumns: COLS }} role="button" tabIndex={0} aria-expanded={!!open[p.n]} onClick={() => setOpen((o) => ({ ...o, [p.n]: !o[p.n] }))} onKeyDown={(e) => e.key === 'Enter' && setOpen((o) => ({ ...o, [p.n]: !o[p.n] }))}>
                <span data-l="N.">
                  <span className="m">{nn(p.n)}</span>
                </span>
                <span data-l="SCARTO">
                  <span className="m">{p.scarto.n}</span>
                </span>
                <span data-l="PROPOSTA">
                  <b>{p.titolo}</b>
                </span>
                <span data-l="CHI LA FA">
                  <span className="chip cy">{PROPOSAL_WHO_TEXT[p.chi].toUpperCase()}</span>
                </span>
                <span data-l="PROGRAMMA">{p.programma ?? '—'}</span>
                <span data-l="BENEFICIO">
                  <span className="note">{p.intangibili[0]?.testo ?? (p.sanzioni[0] ? 'sanzione evitata' : '—')}</span>
                </span>
              </div>
              {open[p.n] && (
                <div className="row-open">
                  <Proposal p={p} />
                </div>
              )}
              </Fragment>
            ))}
          </div>
          <p className="note">Le proposte senza beneficio tangibile hanno i benefici intangibili con il perché e la fonte, aprendole.</p>
        </section>
      )}

      {d.ore_per_ruolo.length > 0 && (
        <section className="pan">
          <div className="hd">
            <h2 className="h2">Ore liberate, per reparto e per ruolo</h2>
          </div>
          <Rows cols="minmax(0,1.4fr) minmax(0,1.4fr) 140px 70px" head={['REPARTO', 'RUOLO', 'ORE AL MESE', 'DA']}>
            {[
              ...d.ore_per_ruolo.map((r) => [
                r.reparto,
                r.ruolo,
                <b key="o" className="m">
                  {range(r.ore)}
                </b>,
                r.da.map(nn).join(', '),
              ]),
              ...(d.totale_ore
                ? [
                    [
                      <b key="t">Totale</b>,
                      '',
                      <span key="o">
                        <b className="m">almeno {it(d.totale_ore.da)}</b>
                        <span className="d m">intervallo {range(d.totale_ore)}</span>
                      </span>,
                      '',
                    ],
                  ]
                : []),
            ]}
          </Rows>
        </section>
      )}

      {d.timonio && <TaskMap m={d.timonio} tutte={tutte} />}
    </>
  );
}

/** I totali «almeno» della fase 11: proposte, ore, euro e sanzioni evitate. */
function Totals({ d }: { d: PhaseElevenDto }) {
  return (
    <section className="kpis">
        <Kpi label="PROPOSTE" n={String(d.numeri.proposte)} src="UNA PER SCARTO" />
        <Kpi label="ORE LIBERATE" n={d.numeri.ore ? `almeno ${it(Math.round(d.numeri.ore.da), 0)}` : '—'} src={d.numeri.ore ? `ORE AL MESE · INTERVALLO PRUDENTE ${range(d.numeri.ore, 0)}` : 'NESSUNA ORA CON IL CALCOLO'} small />
        <Kpi label="EURO" n={d.numeri.euro ? `almeno € ${it(d.numeri.euro.da, 0)}` : '—'} src={d.numeri.euro ? `AL MESE · INTERVALLO € ${range(d.numeri.euro, 0)}` : 'SENZA COSTO ORARIO CON UNA FONTE'} small />
        <Kpi label="SANZIONI EVITATE" n={d.numeri.sanzioni ? `almeno € ${it(d.numeri.sanzioni.minimo, 0)}` : '—'} src={d.numeri.sanzioni ? `SOMMA DEI MINIMI DI LEGGE, ${d.numeri.sanzioni.obblighi} ${d.numeri.sanzioni.obblighi === 1 ? 'OBBLIGO' : 'OBBLIGHI'}` : 'NESSUNA CON L’IMPORTO'} gold small />
      </section>
  );
}

/** Il beneficio di una proposta in poche parole, per la sintesi. */
const benefitShort = (p: ClosingProposalDto) => {
  if (p.beneficio) return `almeno ${it(p.beneficio.ore.da)} h al mese${p.beneficio.euro ? ` · € ${it(p.beneficio.euro.da, 0)}` : ''}`;
  const min = p.sanzioni.flatMap((x) => (x.minimo ? [x.minimo] : []));
  if (min.length) return `sanzione evitata: almeno € ${it(Math.min(...min), 0)}`;
  return p.intangibili[0]?.testo ?? '—';
};

/**
 * Le proposte in sintesi (fase 11, consegna): i totali «almeno», le prime cinque proposte del percorso in una riga, Timonio
 * nell'azienda in un riquadro. Il dettaglio sta nell'allegato.
 */
export function ProposalsSummary({ d }: { d: PhaseElevenDto }) {
  const top = d.proposte.slice(0, 5);
  const m = d.timonio;
  return (
    <>
      <Totals d={d} />
      <div className="hd">
        <h2 className="h2">Il percorso, in sintesi</h2>
      </div>
      <p className="sub">Le proposte che contano di più, in ordine di priorità: prima gli obblighi scoperti, poi ciò che pesa di più in ore ed euro, poi ciò che riduce un rischio.</p>
      <Rows cols="40px minmax(0,2fr) 140px minmax(0,1fr)" head={['N.', 'PROPOSTA', 'CHI LA FA', 'BENEFICIO']}>
        {top.map((p) => [
          <span key="n" className="m">
            {nn(p.n)}
          </span>,
          <span key="t">
            <b>{p.titolo}</b>
            <span className="d">{p.cosa_cambia}</span>
          </span>,
          <span key="c" className="chip cy">
            {PROPOSAL_WHO_TEXT[p.chi].toUpperCase()}
          </span>,
          <span key="b" className={p.beneficio ? 'm' : 'note'}>
            {benefitShort(p)}
          </span>,
        ])}
      </Rows>
      {d.proposte.length > top.length && <p className="note">E altre {d.proposte.length - top.length} proposte, nell’allegato.</p>}
      {m ? (
        <section className="pan">
          <div className="hd">
            <h2 className="h2">Timonio nell’azienda</h2>
            <span className="lbl">{m.classificati} COMPITI</span>
          </div>
          <div className="chips" style={{ marginBottom: 14 }}>
            {AUTOMATION_GRADES.map((g) => (
              <span key={g} className={`chip ${GRADE_CHIP[g]}`}>
                {g} · {AUTOMATION_GRADE_TEXT[g].toUpperCase()} · {m.gradi[g]}
              </span>
            ))}
          </div>
          <section className="kpis">
            <Kpi label="GRADI A E B" n={m.ab ? `almeno ${it(m.ab.ore.da)}` : '—'} src={m.ab ? 'ORE AL MESE' : 'NESSUN COMPITO A O B'} small />
            <Kpi label="EURO A E B" n={m.ab?.euro ? `almeno € ${it(m.ab.euro.da, 0)}` : '—'} src={m.ab?.euro ? 'AL MESE · BASE DEL PREZZO' : 'SENZA COSTO ORARIO CON UNA FONTE'} small />
            <Kpi label="GRADO C, A PARTE" n={m.c ? `almeno ${it(m.c.ore.da)}` : '—'} src={m.c ? 'ORE AL MESE · NON ENTRA NEL PREZZO' : 'NESSUN COMPITO C'} gold small />
          </section>
          <p className="note">Il dettaglio per processo, procedura e ruolo, con il calcolo di ogni compito, è nell’allegato.</p>
        </section>
      ) : (
        !!d.novita_in_corso?.length && (
          <section className="pan">
            <div className="hd">
              <h2 className="h2">Timonio nell’azienda</h2>
              <span className="lbl">NOVITÀ DEL METODO</span>
            </div>
            <p className="note">
              <span className="sn-dot spin" aria-hidden /> Preparo {d.novita_in_corso.join(', ')}: do un grado a ogni compito dei ruoli e a ogni passaggio delle procedure. Il resto della fase resta com’è.
            </p>
          </section>
        )
      )}
    </>
  );
}

const GRADE_CHIP: Record<AutomationGrade, string> = { A: 'cy', B: 'vi', C: 'gold', D: 'dim' };
const sumText = (x: TaskSumDto | null) => (x ? `almeno ${it(x.ore.da)} h${x.euro ? ` · € ${it(x.euro.da, 0)}` : ''}` : '—');
const MAP_COLS = 'minmax(0,1.6fr) 44px 44px 44px 44px minmax(0,1.1fr) minmax(0,1fr)';

/**
 * La mappa «Timonio nell'azienda» (art. 3.47; fase 11, consegna): i compiti per ruolo e grado, con il totale «almeno» di A
 * e B (la base del prezzo del contratto n. 2) e il grado C a parte. Ogni ruolo si apre sui suoi compiti, ognuno con il
 * calcolo; `tutte`: ogni ruolo aperto, come nel documento.
 */
export function TaskMap({ m, tutte = false }: { m: TaskMapDto; tutte?: boolean }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (r: string) => setOpen((o) => ({ ...o, [r]: !o[r] }));
  const [view, setView] = useState<'processi' | 'ruoli'>(m.collegata ? 'processi' : 'ruoli');
  return (
    <section className="pan">
      <div className="hd">
        <h2 className="h2">Timonio nell’azienda</h2>
        <span className="lbl">{m.collegata ? 'DAI COMPITI ALLE PROCEDURE E AI PROCESSI' : 'I COMPITI PER RUOLO E GRADO'}</span>
      </div>
      <p className="sub">Ogni compito delle mansioni e ogni passaggio delle procedure ha il suo grado, per regola uguale per ogni azienda, e il suo valore con il calcolo.</p>
      <div className="chips" style={{ marginBottom: 14 }}>
        {AUTOMATION_GRADES.map((g) => (
          <span key={g} className={`chip ${GRADE_CHIP[g]}`}>
            {g} · {AUTOMATION_GRADE_TEXT[g].toUpperCase()} · {m.gradi[g]}
          </span>
        ))}
      </div>
      <section className="kpis">
        <Kpi label="GRADI A E B" n={m.ab ? `almeno ${it(m.ab.ore.da)}` : '—'} src={m.ab ? `ORE AL MESE · ${m.ab.compiti} COMPITI` : 'NESSUN COMPITO A O B'} small />
        <Kpi label="EURO A E B" n={m.ab?.euro ? `almeno € ${it(m.ab.euro.da, 0)}` : '—'} src={m.ab?.euro ? `AL MESE · INTERVALLO € ${range(m.ab.euro, 0)}` : 'SENZA COSTO ORARIO CON UNA FONTE'} small />
        <Kpi label="GRADO C, A PARTE" n={m.c ? `almeno ${it(m.c.ore.da)}` : '—'} src={m.c ? `ORE AL MESE${m.c.euro ? ` · € ${it(m.c.euro.da, 0)}` : ''} · NON ENTRA NEL PREZZO` : 'NESSUN COMPITO C'} gold small />
        <Kpi label="COMPITI" n={`${m.classificati}/${m.totali}`} src="CLASSIFICATI" small />
      </section>
      {m.collegata && !tutte && (
        <div className="chips" role="tablist" style={{ marginBottom: 12 }}>
          {(['processi', 'ruoli'] as const).map((x) => (
            <button key={x} type="button" role="tab" aria-selected={view === x} className={`chip ${view === x ? 'cy' : 'dim'}`} style={{ cursor: 'pointer', background: 'none' }} onClick={() => setView(x)}>
              {x === 'processi' ? 'PER PROCESSO' : 'PER RUOLO'}
            </button>
          ))}
        </div>
      )}
      {m.collegata && (tutte || view === 'processi') && <ProcessMap m={m} tutte={tutte} />}
      {(!m.collegata || tutte || view === 'ruoli') && (
      <div className="rows">
        <div className="row head" style={{ gridTemplateColumns: MAP_COLS }}>
          {['RUOLO', 'A', 'B', 'C', 'D', 'A E B AL MESE', 'C AL MESE'].map((h) => (
            <span key={h}>{h}</span>
          ))}
        </div>
        {m.ruoli.map((r) => (
          <div key={r.ruolo} style={{ display: 'contents' }}>
            <div className="row click" style={{ gridTemplateColumns: MAP_COLS }} role="button" tabIndex={0} aria-expanded={tutte || !!open[r.ruolo]} onClick={() => toggle(r.ruolo)} onKeyDown={(e) => e.key === 'Enter' && toggle(r.ruolo)}>
              <span data-l="RUOLO">
                <b>{r.ruolo}</b>
                {r.conta && <span className="d">conta la vista delle {r.conta}</span>}
              </span>
              {AUTOMATION_GRADES.map((g) => (
                <span key={g} data-l={g} className="m">
                  {r.gradi[g] || '·'}
                </span>
              ))}
              <span data-l="A E B AL MESE" className="m">
                {sumText(r.ab)}
              </span>
              <span data-l="C AL MESE" className="m">
                {sumText(r.c)}
              </span>
            </div>
            {(tutte || open[r.ruolo]) && <Tasks list={r.compiti} />}
          </div>
        ))}
        {(m.ab || m.c) && (
          <div className="row" style={{ gridTemplateColumns: MAP_COLS }}>
            <span data-l="RUOLO">
              <b>Totale</b>
            </span>
            {AUTOMATION_GRADES.map((g) => (
              <span key={g} data-l={g} className="m">
                {m.gradi[g]}
              </span>
            ))}
            <span data-l="A E B AL MESE">
              <b className="m">{sumText(m.ab)}</b>
              {m.ab && <span className="d m">intervallo {range(m.ab.ore)} h</span>}
            </span>
            <span data-l="C AL MESE" className="m">
              {sumText(m.c)}
            </span>
          </div>
        )}
      </div>
      )}
      {m.note.map((x) => (
        <p key={x} className="note">
          {x}
        </p>
      ))}
    </section>
  );
}

const PROC_COLS = 'minmax(0,1.8fr) minmax(0,1fr) minmax(0,1.1fr) minmax(0,1fr)';

/**
 * La mappa per processo (secondo passo): i processi per reparto e sede, poi le procedure degli obblighi e i compiti fuori
 * dalle procedure; ogni processo si apre sulle procedure, ogni procedura sui passaggi con il calcolo.
 */
function ProcessMap({ m, tutte }: { m: TaskMapDto; tutte: boolean }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (k: string) => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const where = (p: TaskProcessDto) => (p.reparto ? `${p.reparto}${p.sede ? ` · ${p.sede}` : ''}` : p.tipo === 'fuori' ? 'ruolo per ruolo' : p.tipo === 'obblighi' ? 'dagli obblighi di legge' : (p.sede ?? '—'));
  return (
    <div className="rows" style={{ marginBottom: 16 }}>
      <div className="row head" style={{ gridTemplateColumns: PROC_COLS }}>
        {['PROCESSO', 'REPARTO E SEDE', 'A E B AL MESE', 'C AL MESE'].map((h) => (
          <span key={h}>{h}</span>
        ))}
      </div>
      {m.processi.map((p, i) => {
        const k = `p${i}`;
        return (
          <div key={k} style={{ display: 'contents' }}>
            <div className="row click" style={{ gridTemplateColumns: PROC_COLS }} role="button" tabIndex={0} aria-expanded={tutte || !!open[k]} onClick={() => toggle(k)} onKeyDown={(e) => e.key === 'Enter' && toggle(k)}>
              <span data-l="PROCESSO">
                <b>{p.nome}</b>
                <span className="d">
                  {p.procedure.length} {p.tipo === 'fuori' ? (p.procedure.length === 1 ? 'ruolo' : 'ruoli') : p.procedure.length === 1 ? 'procedura' : 'procedure'}
                </span>
              </span>
              <span data-l="REPARTO E SEDE">{where(p)}</span>
              <span data-l="A E B AL MESE" className="m">
                {sumText(p.ab)}
              </span>
              <span data-l="C AL MESE" className="m">
                {sumText(p.c)}
              </span>
            </div>
            {(tutte || open[k]) &&
              p.procedure.map((x, j) => {
                const kk = `${k}-${j}`;
                return (
                  <div key={kk} style={{ display: 'contents' }}>
                    <div className="row click" style={{ gridTemplateColumns: PROC_COLS, paddingLeft: 18 }} role="button" tabIndex={0} aria-expanded={tutte || !!open[kk]} onClick={() => toggle(kk)} onKeyDown={(e) => e.key === 'Enter' && toggle(kk)}>
                      <span data-l={p.tipo === 'fuori' ? 'RUOLO' : 'PROCEDURA'}>
                        {x.nome}
                        {x.anche_in.length > 0 && <span className="d">anche in {x.anche_in.join(', ')}: contata qui</span>}
                      </span>
                      <span data-l="PARTE DI TIMONIO" className="m">
                        {x.copre.abc} su {x.copre.totale} {p.tipo === 'fuori' ? 'compiti' : 'passaggi'} con Timonio
                      </span>
                      <span data-l="A E B AL MESE" className="m">
                        {sumText(x.ab)}
                      </span>
                      <span data-l="C AL MESE" className="m">
                        {sumText(x.c)}
                      </span>
                    </div>
                    {(tutte || open[kk]) && <Tasks list={x.compiti} />}
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}

/** I compiti nella mappa: grado, perché e, per A, B e C, il calcolo dello studio; quelli contati in un passaggio lo dicono. */
function Tasks({ list }: { list: TaskValueDto[] }) {
  return (
    <div style={{ padding: '6px 0 14px' }}>
      {list.map((v) => (
        <div key={v.rif} className="row" style={{ gridTemplateColumns: '44px minmax(0,1fr) 140px', alignItems: 'start' }}>
          <span>
            <span className={`chip ${GRADE_CHIP[v.grado]}`}>{v.grado}</span>
          </span>
          <span>
            <b>{v.testo}</b>
            <span className="d">
              {v.da === 'mansione' ? 'mansione' : ''} {v.dove} · {v.perche}
            </span>
            {v.beneficio && !v.contato_in && (
              <span className="calc" style={{ display: 'block', marginTop: 6 }}>
                {v.beneficio.righe.map((x) => (
                  <span key={x}>
                    {x}
                    <br />
                  </span>
                ))}
                <span className="i">{v.beneficio.ipotesi}</span>
              </span>
            )}
          </span>
          <span className="m">{v.contato_in ? <span className="d">contato in {v.contato_in}</span> : v.beneficio ? `${range(v.beneficio.ore)} h${v.beneficio.euro ? ` · € ${range(v.beneficio.euro, 0)}` : ''}` : '—'}</span>
        </div>
      ))}
    </div>
  );
}

const COLS = '40px 50px minmax(0,1.5fr) 130px minmax(0,1fr) minmax(0,1fr)';

function Proposal({ p }: { p: ClosingProposalDto }) {
  const kv = (rows: Array<[string, React.ReactNode]>) => (
    <div className="rows kv">
      {rows.map(([k, v]) => (
        <div key={k} className="row" style={{ gridTemplateColumns: '150px minmax(0,1fr)' }}>
          <span className="k">{k}</span>
          <span>{v}</span>
        </div>
      ))}
    </div>
  );
  const b = p.beneficio;
  return (
    <article className={`pan ${p.obbligo ? 'cy' : ''}`}>
      <div className="hd">
        <h3 className="h3" style={{ fontSize: 16 }}>
          <span className="m" style={{ color: '#3cf0ff' }}>
            {nn(p.n)}
          </span>{' '}
          · {p.titolo}
        </h3>
        <span className="chips">
          <span className="chip">
            SCARTO {p.scarto.n} · {p.scarto.certezza.toUpperCase()}
            {p.scarto.fonte?.includes('· T') ? ' (RISPOSTA TUA)' : ''}
          </span>
        </span>
      </div>
      {kv([
        ['Cosa cambia', p.cosa_cambia],
        ['Chi la fa', <span className="chip cy">{PROPOSAL_WHO_TEXT[p.chi].toUpperCase()}</span>],
        ['Con quale programma', p.programma ?? '—'],
      ])}
      <p className="lbl">BENEFICIO TANGIBILE</p>
      {b && (
        <>
          <p style={{ font: '700 20px Unbounded,sans-serif' }}>
            {range(b.ore)} ore al mese{b.euro ? ` · € ${range(b.euro, 0)}` : ''}
          </p>
          <div className="calc">
            {b.righe.map((r) => (
              <span key={r}>
                {r}
                <br />
              </span>
            ))}
            <span className="i">{b.ipotesi}</span>
          </div>
        </>
      )}
      {p.sanzioni.map((x) => (
        <span key={x.testo} style={{ display: 'contents' }}>
          <p style={{ font: '700 20px Unbounded,sans-serif' }}>sanzione evitata{x.minimo ? `: almeno € ${it(x.minimo, 0)}` : ''}{x.massimo ? <span className="note"> · fino a € {it(x.massimo, 0)}</span> : null}</p>
          <div className="calc">
            {x.testo} <span className="i">· {x.norma}</span>
          </div>
        </span>
      ))}
      {!b && !p.sanzioni.length && <p className="note">Nessun beneficio tangibile: solo quelli qui sotto.</p>}
      {p.intangibili.length > 0 && (
        <>
          <p className="lbl">BENEFICI INTANGIBILI</p>
          {p.intangibili.map((x) => (
            <p key={x.testo} style={{ fontSize: 14, color: '#c3cbe0' }}>
              {x.testo}: {x.perche} <span className="src">· {x.fonte.toUpperCase()}</span>
            </p>
          ))}
        </>
      )}
      <p className="lbl">NEL PERCORSO</p>
      {kv([
        ['Punto di partenza', p.percorso.partenza],
        ['Traguardo', p.percorso.traguardo],
        ['Cosa serve', p.percorso.cosa_serve],
        ['Cosa misura che è fatta', p.percorso.misura],
      ])}
    </article>
  );
}

/** L'offerta del contratto n. 2 (art. 4.4) con il link di prova dei ruoli e la scelta del titolare (art. 12.3). */
function Offer() {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  const o = useTenantQuery<ContractTwoOfferDto>(['consegna', 'offerta'], '/onboarding/consegna/offerta');
  const c = useTenantQuery<EngagementCloseDto>(['consegna', 'chiusura'], '/onboarding/consegna/chiusura');
  const [err, setErr] = useState<string | null>(null);
  const choose = (scelta: 'accettata' | 'rimandata' | 'rifiutata') =>
    api<EngagementCloseDto>(t('/onboarding/consegna/offerta'), { method: 'POST', body: { scelta } })
      .then(() => qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'consegna'] }))
      .catch((e) => setErr(errorMessage(e)));
  const makeLink = () =>
    api(t('/onboarding/timone/prova'), { method: 'POST' })
      .then(() => qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'consegna'] }))
      .catch((e) => setErr(errorMessage(e)));
  const d = o.data;
  if (!d) return null;
  return (
    <section className="pan vi" id="offerta">
      <div className="hd">
        <h2 className="h2">Timonio al lavoro: l’offerta del contratto n. 2</h2>
        <span className="lbl vi">NON IMPEGNA</span>
      </div>
      {d.perche && <p className="note">{d.perche}</p>}
      {d.ruoli.length > 0 && (
        <Rows cols="minmax(0,1fr) minmax(0,1.6fr) 120px 120px" head={['RUOLO', 'COSA FAREBBE TIMONIO OGNI GIORNO', 'ORE AL MESE', 'EURO AL MESE']}>
          {d.ruoli.map((r) => [
            <b key="r">{r.ruolo}</b>,
            <span key="l">
              {r.lavori.map((l, i) => (
                <span key={i} className="d">
                  {l.grado} · {l.testo}
                </span>
              ))}
            </span>,
            <span key="o" className="m">
              {r.ore ? `almeno ${it(r.ore.da)}` : '—'}
              {r.ore && <span className="d">{range(r.ore)}</span>}
            </span>,
            <span key="e" className="m">
              {r.euro ? `almeno € ${it(r.euro.da, 0)}` : '—'}
              {r.euro && <span className="d">€ {range(r.euro, 0)}</span>}
            </span>,
          ])}
        </Rows>
      )}
      {d.ritorno.prezzo_mese != null && d.ritorno.valore_mese && (
        <div className="calc">
          valore al mese: almeno € {it(d.ritorno.valore_mese.da, 0)} (intervallo € {range(d.ritorno.valore_mese, 0)})
          <br />
          prezzo del listino: {Math.round(d.ritorno.quota * 100)}% di € {it(d.ritorno.valore_mese.da, 0)} = € {it(d.ritorno.prezzo_mese, 0)} al mese, con {d.ritorno.prova_giorni} giorni di prova
          <br />
          si ripaga in {d.ritorno.giorni_per_ripagare} {d.ritorno.giorni_per_ripagare === 1 ? 'giorno lavorativo' : 'giorni lavorativi'}
          <br />
          <span className="i">{d.ritorno.formula}</span>
        </div>
      )}
      {d.grado_c && (
        <p className="note">
          Il grado C (la persona lo fa con Timonio) vale almeno {it(d.grado_c.ore.da)} ore al mese{d.grado_c.euro ? `, almeno € ${it(d.grado_c.euro.da, 0)}` : ''}: si mostra come beneficio, ma non entra nel prezzo.
        </p>
      )}
      {d.sanzioni.length > 0 && (
        <p className="note">
          Le sanzioni evitate non entrano nel prezzo: {d.sanzioni.map((x) => `${nn(x.proposta)} · ${x.testo} (${x.norma})`).join('; ')}.
        </p>
      )}
      <p className="lbl">LA PROVA DEI RUOLI</p>
      {d.prova ? (
        <p style={{ fontSize: 14 }}>
          Ogni persona dell’azienda prova la dashboard del suo ruolo, senza dare i suoi dati: <a href={d.prova.url}>{d.prova.url}</a>
        </p>
      ) : (
        <div className="chips">
          <span className="note">Crea il link e giralo alle tue persone: ognuna prova la dashboard del suo ruolo, senza dare i suoi dati.</span>
          {isManager && (
            <button type="button" className="btn2" onClick={() => void makeLink()}>
              Crea il link di prova
            </button>
          )}
        </div>
      )}
      <p className="note">{d.nota}</p>
      {c.data && (
        <>
          {!c.data.eseguito && <p className="note">Per dire eseguito l’incarico manca ancora: {c.data.manca.join(' ')}</p>}
          {c.data.offerta && (
            <p className="note">
              Offerta {c.data.offerta} il {new Date(c.data.offerta_il!).toLocaleDateString('it-IT')}.
            </p>
          )}
        </>
      )}
      {isManager && d.pronta && (
        <div className="chips">
          <button type="button" className="btn" onClick={() => void choose('accettata')}>
            Accetto l’offerta
          </button>
          <button type="button" className="btn2" onClick={() => void choose('rimandata')}>
            Ci penso
          </button>
          <button type="button" className="btn2" onClick={() => void choose('rifiutata')}>
            No, grazie
          </button>
        </div>
      )}
      {err && <p className="err">{err}</p>}
    </section>
  );
}
